/**
 * What the workbench keeps for this Agent, drawn among the plugins' settings.
 *
 * The plugins declare their own pages (`directory.ts`); these are the shell's:
 * the monthly spending reminder, the developer view and -- only where no Hub
 * holds 账号 -- the appearance. They join the groups the plugins already
 * coordinate on, under the same names, so an owner finds the reminder beside
 * the rest of 预算与授权 rather than on a page of its own.
 */

import { useEffect, useRef, useState } from "react";
import { api, type PanelEntry, type PanelProblem } from "../lib/api";
import { useDraft } from "../lib/drafts";
import { currencyCode, withUnit } from "../lib/money";
import { LEDGER_PAGES } from "../pages/LedgerPage";
import { AppearanceSettings } from "../shell/AppearanceSettings";
import type { SettingsDestination } from "./directory";
import { leaveSettings } from "./navigation";
import { NumberField, Switch } from "../components/rumonai";

export const SPEND_SETTING = "owner-settings:spend";
export const DEVELOPER_SETTING = "owner-settings:developer";
export const APPEARANCE_SETTING = "owner-settings:appearance";

export interface WorkbenchPrefs {
  /** No Hub above this Agent: appearance has nowhere else to live. */
  appearance: boolean;
  developer: boolean;
  onDeveloper: (next: boolean) => void;
  problems: PanelProblem[];
}

function destination(key: string, title: string, note: string, group: string, groupTitle: string, groupOrder: number, order: number): SettingsDestination {
  return { key, title, note, group, groupOrder, order, owner: key,
    declared: { scope: "agent", title, note, group, group_title: groupTitle, group_order: groupOrder, order } };
}

export function workbenchDestinations(appearance: boolean): SettingsDestination[] {
  return [
    destination(SPEND_SETTING, "用量提醒", "每月花到多少钱时提醒你，只提醒，不会拦住 Agent", "operation", "预算与授权", 40, 30),
    { ...destination(DEVELOPER_SETTING, "开发者视图", "调用记录与诊断", "development", "开发与自检", 90, 0),
      declared: { ...destination(DEVELOPER_SETTING, "开发者视图", "调用记录与诊断", "development", "开发与自检", 90, 0).declared, advanced: true } },
    ...(appearance ? [destination(APPEARANCE_SETTING, "外观", "颜色、明暗和背景图案", "preferences", "偏好与表达", 20, 0)] : []),
  ];
}

export function WorkbenchSetting({ which, prefs, panels, onToast }: {
  which: string;
  prefs: WorkbenchPrefs;
  panels: PanelEntry[];
  onToast: (message: string, tone: "good" | "critical") => void;
}) {
  if (which === SPEND_SETTING) return <SpendLimit onToast={onToast} />;
  if (which === DEVELOPER_SETTING) return <DeveloperSetting prefs={prefs} panels={panels} />;
  return prefs.appearance ? <AppearanceSettings /> : null;
}

function SpendLimit({ onToast }: { onToast: (message: string, tone: "good" | "critical") => void }) {
  const [draft, setDraft, draftError] = useDraft<{ limit: string; warnAt: string; currency?: string } | null>("settings:spend", null);
  const [stored, setStored] = useState<{ limit: string; warnAt: string; currency: string } | null>(null);
  const [error, setError] = useState("");
  const savedHere = useRef(false);
  const { limit, warnAt } = draft ?? stored ?? { limit: "", warnAt: "" };
  // The currency the Agent already knows (the limit's own, else the platform's) is shown, not asked for.
  const known = stored?.currency ?? "";
  const currency = known || draft?.currency || "";
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    api.prefs().then((prefs) => {
      if (!live || savedHere.current) return;
      const amount = prefs.spend_limit;
      const warn = prefs.spend_warn_at;
      setStored({ limit: typeof amount === "number" && amount > 0 ? String(amount) : "",
        warnAt: typeof warn === "number" && warn > 0 ? String(Math.round(warn * 100)) : "",
        currency: currencyCode(prefs.spend_limit_currency) || currencyCode(prefs.platform_currency) });
    }).catch(() => { if (live && !savedHere.current) setError("暂时无法读取用量提醒，请重新打开这一页。"); });
    return () => { live = false; };
  }, []);
  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const percent = Number(warnAt);
      const amount = limit.trim() === "" ? 0 : Number(limit);
      const code = currencyCode(currency);
      if (amount > 0 && !code) throw new Error("币种请填写三位代码，例如 USD 或 CNY。");
      await api.savePrefs({
        spend_limit: amount,
        spend_limit_currency: code || null,
        spend_warn_at: Number.isFinite(percent) && percent > 0 ? percent / 100 : null,
      });
      savedHere.current = true;
      setStored({ limit, warnAt, currency: code });
      setDraft(null);
      setError("");
      onToast("已保存", "good");
    } catch (err) {
      onToast(`未能保存：${(err as Error).message}`, "critical");
    } finally {
      setBusy(false);
    }
  };
  return <section className="connection-detail" aria-label="用量提醒">
    <header><h2>用量提醒</h2></header>
    <p className="connection-intro">只提醒，不限制执行。</p>
    {error || draftError ? <p role="alert">{error || draftError}</p> : null}
    <fieldset className="connection-fields" disabled={busy || (!stored && !draft)}>
      <div className="form">
        <div className="form-field form-field-short">
          <label className="field-label micro" htmlFor="spend-limit">{withUnit("每月提醒金额", currency)}</label>
          <NumberField id="spend-limit" name="spend_limit" label={withUnit("每月提醒金额", currency)} min="0" step="1" value={limit} data-testid="spend-limit"
            aria-describedby="spend-limit-help" onValueChange={(next) => setDraft({ limit: next === null ? "" : String(next), warnAt, currency })} />
          <p className="field-help micro" id="spend-limit-help">留空则不提醒。</p>
        </div>
        {known ? null : <div className="form-field form-field-short">
          <label className="field-label micro" htmlFor="spend-currency">币种（三位代码）</label>
          <span className="pa-field-shell"><input id="spend-currency" name="spend_limit_currency" type="text" maxLength={3} placeholder="例如 USD" value={currency}
            data-testid="spend-currency" onChange={(event) => setDraft({ limit, warnAt, currency: event.target.value.toUpperCase() })} /><span className="pa-paint-edge" aria-hidden="true" /></span>
        </div>}
        <div className="form-field form-field-short">
          <label className="field-label micro" htmlFor="spend-warn-at">提前提醒（%）</label>
          <NumberField id="spend-warn-at" name="spend_warn_at" label="提前提醒（%）" min="1" max="100" step="1" value={warnAt} data-testid="spend-warn-at"
            aria-describedby="spend-warn-at-help" onValueChange={(next) => setDraft({ limit, warnAt: next === null ? "" : String(next), currency })} />
          <p className="field-help micro" id="spend-warn-at-help">到这个比例先提醒，默认 80%。</p>
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-accent" data-testid="spend-save" onClick={() => void save()}>
            {busy ? "正在保存…" : "保存"}
          </button>
        </div>
      </div>
    </fieldset>
  </section>;
}

function DeveloperSetting({ prefs, panels }: { prefs: WorkbenchPrefs; panels: PanelEntry[] }) {
  return <section className="connection-detail" aria-label="开发者视图">
    <header><h2>开发者视图</h2></header>
    <fieldset className="connection-fields">
      <div className="form">
        <div className="form-field">
          <Switch id="developer-switch" name="developer" label="显示开发者视图" checked={prefs.developer} data-testid="developer-switch"
            aria-describedby="developer-switch-help" onCheckedChange={prefs.onDeveloper} />
          <p className="field-help micro" id="developer-switch-help">显示调用记录与诊断页面。</p>
        </div>
      </div>
    </fieldset>
    {/* The console's own pages and every registered plugin page, in one place;
        they open in the Agent's workspace, not in these settings. */}
    {prefs.developer ? <nav className="connection-actions" aria-label="诊断与记录">
      <h3>诊断与记录</h3>
      <div className="hub-actions">
        <a className="btn" {...leaveSettings("#/capabilities")}>已接入能力</a>
        <a className="btn" {...leaveSettings("#/timeline")}>执行时间线</a>
        <a className="btn" {...leaveSettings("#/approvals")}>审批记录</a>
        {LEDGER_PAGES.map(page => <a className="btn" key={page.name} {...leaveSettings(`#/ledger/${page.name}`)}>{page.title}</a>)}
        {panels.map(panel => <a className="btn" key={panel.key} {...leaveSettings(`#/panel/${encodeURIComponent(panel.key)}`)}>{panel.title}</a>)}
      </div>
      {prefs.problems.map(problem => <p role="alert" key={problem.key}>{problem.key}：{problem.problem}</p>)}
    </nav> : null}
  </section>;
}
